import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import { HapticLink as Link } from '../../utils/haptics';
import { styles } from '../../styles/styles.js';
import { getHealth, getOrders, getPerformance, getBankrollHistory } from '../../services/tradingApi';

const StatCard = ({ label, value, color }) => (
  <Card style={{ ...styles.cardbutton_solo, marginBottom: '10px', flex: '1 1 200px' }}>
    <Card.Body>
      <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>{label}</div>
      <div style={{ color: color || 'white', fontSize: '24px', fontWeight: 'bold' }}>{value}</div>
    </Card.Body>
  </Card>
);

const QuickLink = ({ to, title }) => (
  <Link to={to} style={{ textDecoration: 'none', flex: '1 1 200px' }}>
    <Card style={{ ...styles.cardbutton_solo, marginBottom: '10px', cursor: 'pointer' }}>
      <Card.Body>
        <Card.Title style={{ color: 'white', margin: 0 }}>{title}</Card.Title>
      </Card.Body>
    </Card>
  </Link>
);

const TradingDashboard = () => {
  const [health, setHealth] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [performance, setPerformance] = useState(null);
  const [bankroll, setBankroll] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [healthRes, ordersRes, perfRes, bankrollRes] = await Promise.allSettled([
          getHealth(),
          getOrders({ status: 'prepared', limit: 1 }),
          getPerformance({ days: 1 }),
          getBankrollHistory({ limit: 1 }),
        ]);

        if (healthRes.status === 'fulfilled') setHealth(healthRes.value.data);
        if (ordersRes.status === 'fulfilled') setPendingCount(ordersRes.value.data.total);
        if (perfRes.status === 'fulfilled') setPerformance(perfRes.value.data);
        if (bankrollRes.status === 'fulfilled') {
          const entries = bankrollRes.value.data;
          if (entries.length > 0) setBankroll(entries[entries.length - 1]);
        }
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  const serviceStatus = health?.status === 'running' ? 'Online' : 'Offline';
  const statusColor = health?.status === 'running' ? '#4caf50' : '#f44336';
  const todayPnl = performance?.total_pnl ?? 0;
  const pnlColor = todayPnl >= 0 ? '#4caf50' : '#f44336';
  const currentBankroll = bankroll?.balance_after ?? '--';

  if (loading) {
    return <div style={{ color: 'white', padding: '2rem' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1200px' }}>
      <h2 style={{ color: 'white', marginBottom: '20px' }}>Trading Dashboard</h2>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
        <StatCard label="Service Status" value={serviceStatus} color={statusColor} />
        <StatCard label="Pending Orders" value={pendingCount} color={pendingCount > 0 ? '#ff9800' : 'white'} />
        <StatCard label="Today's P&L" value={`$${todayPnl.toFixed(2)}`} color={pnlColor} />
        <StatCard label="Bankroll" value={typeof currentBankroll === 'number' ? `$${currentBankroll}` : currentBankroll} />
      </div>

      {health && (
        <Card style={{ ...styles.cardbutton_solo, marginBottom: '20px' }}>
          <Card.Body>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>Service Details</div>
            <div style={{ color: 'white', fontSize: '14px' }}>
              <span>Enabled: {health.enabled ? 'Yes' : 'No'}</span>
              <span style={{ marginLeft: '20px' }}>Dry Run: {health.dry_run ? 'Yes' : 'No'}</span>
              {health.uptime_seconds && (
                <span style={{ marginLeft: '20px' }}>
                  Uptime: {Math.floor(health.uptime_seconds / 3600)}h {Math.floor((health.uptime_seconds % 3600) / 60)}m
                </span>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <QuickLink to="/trading/orders" title="Order Queue" />
        <QuickLink to="/trading/positions" title="Positions" />
        <QuickLink to="/trading/performance" title="Performance" />
        <QuickLink to="/trading/settings" title="Settings" />
        <QuickLink to="/trading/terminal" title="Terminal" />
      </div>
    </div>
  );
};

export default TradingDashboard;
